/**
 * MemberProfileDialog - rich read-only profile header + editable custom
 * attributes for one team member.
 *
 * The identity block (name, email, roles, owner status, inspector type,
 * activity) is read-only; the tenant-defined custom fields below are edited
 * through the shared EditableDetailCard -> EditFormDialog pattern (never a
 * hand-rolled form). Values are stored on the tenant-scoped Membership join,
 * never on the locked Tier 3 user entity.
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { toast } from 'sonner'
import { Loader2, ListChecks } from 'lucide-react'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Badge } from '@/shared/components/ui/badge'
import { EditableDetailCard } from '@/admin/components/shared/editable-detail-card'
import type {
  CardFieldItem,
  EditConfig,
} from '@/admin/components/shared/editable-card-types'
import { normaliseClaimField } from '@/shared/lib/forms'
import { useFormatters } from '@/shared/lib/formatting'
import { usePermissions } from '@/admin/hooks/use-permissions'
import { toastApiError } from '@/shared/lib/api-error'
import {
  useMemberProfile,
  usePreviewMemberAttributeValues,
  useUpdateMemberAttributeValues,
  type MemberProfile,
} from '@/admin/hooks/use-membership-attributes'
import {
  decorateGeoZoneOptions,
  fieldErrorsFromApiError,
} from '@/admin/pages/settings/member-attribute-helpers'
import { useClaimMovesConfirm } from '@/admin/pages/settings/use-claim-moves-confirm'

interface Props {
  membershipUuid: string | null
  /** Fallback header details while the detail request is in flight. */
  fallbackName?: string
  fallbackEmail?: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

function initialsOf(name: string, email: string): string {
  const source = (name || email || '').trim()
  if (!source) return '?'
  const parts = source.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return source.slice(0, 2).toUpperCase()
}

export function MemberProfileDialog({
  membershipUuid,
  fallbackName,
  fallbackEmail,
  open,
  onOpenChange,
}: Props) {
  const { t } = useTranslation()
  const { formatDate } = useFormatters()
  const { can } = usePermissions()
  const { data: profile, isPending, isError } = useMemberProfile(
    open ? membershipUuid ?? undefined : undefined,
  )
  const updateValues = useUpdateMemberAttributeValues(membershipUuid ?? undefined)
  const previewValues = usePreviewMemberAttributeValues(membershipUuid ?? undefined)
  const { confirmClaimMoves, claimMovesDialog } = useClaimMovesConfirm()

  const attributeItems = useMemo<CardFieldItem[]>(() => {
    if (!profile) return []
    return profile.attribute_values.map((attr) => ({
      field: normaliseClaimField({
        code_name: attr.code_name,
        display_name: attr.display_name,
        field_type: attr.field_type,
        required: attr.required,
        description: attr.description,
        configuration_data: decorateGeoZoneOptions(attr.configuration_data),
      }),
      value: attr.value,
      // Non-editable definitions display but stay out of the edit form.
      readOnly: !attr.editable,
    }))
  }, [profile])

  const editConfig: EditConfig = {
    onSave: async (values, _reason, helpers) => {
      try {
        // Handing this member a zone can move its open claims to them
        // (WPS-160): list them first and let the operator decide.
        let reassignOpenClaims = false
        const preview = await previewValues.mutateAsync(values)
        if (preview.count > 0) {
          const choice = await confirmClaimMoves(
            preview,
            profile?.user_name || profile?.user_email || '',
          )
          // Cancelled: keep the form open with the operator's edits.
          if (choice === null) return false
          reassignOpenClaims = choice
        }
        const saved = await updateValues.mutateAsync({ values, reassignOpenClaims })
        toast.success(
          saved.reassignedClaims > 0
            ? t(
                'members.attributesSavedClaimsMoved',
                'Profile updated. {{count}} open claim(s) moved.',
                { count: saved.reassignedClaims },
              )
            : t('members.attributesSaved', 'Profile updated.'),
        )
      } catch (err) {
        // A per-field conflict (e.g. the one-RS-per-zone guard, WPS-160 #3)
        // renders inline on the offending zone field so the admin sees which
        // zone clashes and with whom; anything else falls back to a toast.
        const fieldErrors = fieldErrorsFromApiError(err)
        const inline = helpers
          ? Object.keys(fieldErrors).filter((code) =>
              (profile?.attribute_values ?? []).some(
                (attr) => attr.code_name === code,
              ),
            )
          : []
        if (inline.length > 0 && helpers) {
          for (const code of inline) helpers.setFieldError(code, fieldErrors[code])
        } else {
          toastApiError(
            err,
            t('members.attributesSaveError', 'Could not save profile.'),
          )
        }
        throw err
      }
    },
    isPending: previewValues.isPending || updateValues.isPending,
    canEdit: can('users.manage'),
    requireReason: false,
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-full max-w-lg" data-testid="member-profile-dialog">
          <DialogHeader className="mb-4">
            <DialogTitle className="text-[15px] text-[var(--ink)]">
              {t('members.profileTitle', 'Team member')}
            </DialogTitle>
          </DialogHeader>

          {isPending ? (
            <div className="flex flex-col gap-6">
              {(fallbackName || fallbackEmail) && (
                <div className="flex items-center gap-3.5">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--bg-subtle)] text-[13px] uppercase tracking-[0.05em] text-[var(--ink-2)]">
                    {initialsOf(fallbackName ?? '', fallbackEmail ?? '')}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-[15px] text-[var(--ink)]">
                      {fallbackName || fallbackEmail}
                    </p>
                    {fallbackName && fallbackEmail && (
                      <p className="truncate text-[12px] text-[var(--ink-3)]">
                        {fallbackEmail}
                      </p>
                    )}
                  </div>
                </div>
              )}
              <div className="flex justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-[var(--ink-3)]" />
              </div>
            </div>
          ) : isError || !profile ? (
            <p className="clara-small py-8 text-center">
              {t('members.profileLoadError', 'Could not load this member.')}
            </p>
          ) : (
            <div className="flex flex-col gap-6">
              <ProfileHeader profile={profile} formatDate={formatDate} t={t} />

              {attributeItems.length > 0 ? (
                <EditableDetailCard
                  title={t('members.customFields', 'Custom fields')}
                  icon={<ListChecks size={16} />}
                  items={attributeItems}
                  editConfig={editConfig}
                  editButtonTestId="member-profile-edit-attributes"
                />
              ) : (
                <p className="clara-small py-2 text-center text-[var(--ink-3)]">
                  {t(
                    'members.noCustomFields',
                    'No custom fields defined for this member.',
                  )}
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      {claimMovesDialog}
    </>
  )
}

function ProfileHeader({
  profile,
  formatDate,
  t,
}: {
  profile: MemberProfile
  formatDate: (value: string) => string
  t: TFunction
}) {
  const name = profile.user_name || profile.user_email
  const roles =
    profile.roles.length > 0
      ? profile.roles
      : profile.role_name
        ? [
            {
              uuid: profile.role_uuid,
              name: profile.role_name,
              is_admin: false,
              is_primary: true,
            },
          ]
        : []

  return (
    <div>
      <div className="flex items-center gap-3.5">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--bg-subtle)] text-[13px] uppercase tracking-[0.05em] text-[var(--ink-2)]">
          {initialsOf(profile.user_name, profile.user_email)}
        </div>
        <div className="min-w-0">
          <p className="truncate text-[15px] text-[var(--ink)]">{name}</p>
          <p className="truncate text-[12px] text-[var(--ink-3)]">
            {profile.user_email}
          </p>
        </div>
      </div>

      {(roles.length > 0 || profile.is_owner) && (
        <div className="mt-3.5 flex flex-wrap items-center gap-1.5">
          {roles.map((role) => (
            <Badge
              key={role.uuid}
              variant="secondary"
              className="inline-flex rounded-full bg-[var(--bg-subtle)] px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] text-[var(--ink-2)]"
            >
              {role.name}
            </Badge>
          ))}
          {profile.is_owner && (
            <Badge
              variant="secondary"
              className="inline-flex rounded-full bg-[var(--green-bg)] px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] text-[var(--green)]"
            >
              {t('users.owner', 'Owner')}
            </Badge>
          )}
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 border-t-[0.5px] border-t-[var(--rule)] pt-4">
        {profile.inspector_type && (
          <HeaderStat
            label={t('members.inspectorType', 'Inspector type')}
            value={profile.inspector_type}
          />
        )}
        <HeaderStat
          label={t('members.lastLogin', 'Last login')}
          value={profile.last_login ? formatDate(profile.last_login) : '-'}
        />
        <HeaderStat
          label={t('members.memberSince', 'Member since')}
          value={profile.member_since ? formatDate(profile.member_since) : '-'}
        />
      </div>
    </div>
  )
}

function HeaderStat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="mb-1 text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
        {label}
      </p>
      <p className="text-[13px] text-[var(--ink)]">{value}</p>
    </div>
  )
}
